import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { resourceFromAttributes } from '@opentelemetry/resources';

const endpoint = process.env.NEXT_PUBLIC_SIGNOZ_OTLP_HTTP_ENDPOINT;
const ingestKey = process.env.NEXT_PUBLIC_SIGNOZ_INGEST_KEY;
const serviceName = process.env.NEXT_PUBLIC_SIGNOZ_SERVICE_NAME;
const apiUrl = process.env.NEXT_PUBLIC_API_URL;
const deploymentEnv = process.env.NEXT_PUBLIC_DEPLOYMENT_ENV ?? 'local-dev';

// Derive the BE host regex from NEXT_PUBLIC_API_URL so per-env hostnames
// (Tailscale locally, Railway in staging/prod) aren't hard-coded here.
function apiHostRegex(): RegExp | null {
  if (!apiUrl) return null;
  try {
    const host = new URL(apiUrl).host;
    const escaped = host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(escaped);
  } catch {
    return null;
  }
}

// Guard: SigNoz is opt-in. Plain `npm run dev` (no env sourced) is a no-op.
// The ingestion key ships in the public JS bundle — accepted trade-off for a
// write-only, rate-limited key. Use a FE-specific key in SigNoz to isolate
// blast radius from the BE key, and rotate via Vercel env + redeploy if abused.
if (endpoint && ingestKey && serviceName) {
  const exporter = new OTLPTraceExporter({
    url: `${endpoint}/v1/traces`,
    headers: { 'signoz-ingestion-key': ingestKey },
  });

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      'service.name': serviceName,
      'deployment.environment': deploymentEnv,
    }),
    spanProcessors: [new BatchSpanProcessor(exporter)],
  });

  // Default context manager from WebTracerProvider is StackContextManager (no Zone.js).
  // ZoneContextManager caused production RangeError: Maximum call stack size exceeded
  // (mutual recursion in Zone cancelTask / onCancelTask) under heavy PV2/async use.
  provider.register();

  const backendHost = apiHostRegex();
  const propagateTraceHeaderCorsUrls = backendHost ? [backendHost] : [];

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({ propagateTraceHeaderCorsUrls }),
      new XMLHttpRequestInstrumentation({ propagateTraceHeaderCorsUrls }),
      new UserInteractionInstrumentation({ eventNames: ['click', 'input', 'submit'] }),
    ],
  });
}
